import React from 'react';
import dayjs from 'dayjs';
import { EditOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { T, H_TITLE, H_SUB, SECTION_LABEL } from './declarationTheme';
import { TYPES_DOMMAGE, CATEGORIES_PERMIS, natureDepuisAdversaire } from '../../../variables/constants';
import DeclarantBlock from './DeclarantBlock';
import PiecesHomologueBlock from './PiecesHomologueBlock';
import { libellePaysOrganisme } from '../../../variables/paysCedeao';

const formatDate = (v) => v ? dayjs(v).format('DD/MM/YYYY') : '-';
const formatTime = (v) => v ? dayjs(v).format(i18n.t('declaration:recapitulatif.formatHeure')) : '-';

/* ─────────────────────────────────────────────────────────────
   Étape 6 - Récapitulatif + Déclarant + Envoi
   ───────────────────────────────────────────────────────────── */
export default function StepRecapitulatif({
  form, values, victimes, adversaires = [], pays = [], organismes = [], goToStep,
  piecesHomologue, setPiecesHomologue,
}) {
  const { t } = useTranslation('declaration');
  // Absent en modification : les pièces d'un dossier existant se gèrent dans sa GED.
  const piecesTe = values.typeSinistre === 'SURVENU_ETRANGER' && !!setPiecesHomologue;
  const paysLabel = (id) => pays.find(p => p.paysTrackingId === id)?.libelle || '-';
  /* Le pays suit la compagnie jusque dans le recapitulatif : c'est la derniere
     occasion de voir qu'on a pris « CORIS » du mauvais pays avant d'enregistrer. */
  const orgLabel = (id) => {
    const o = organismes.find(x => x.organismeTrackingId === id);
    if (!o) return '-';
    const nom = o.code ? `${o.raisonSociale} (${o.code})` : o.raisonSociale;
    const pays = libellePaysOrganisme(o);
    return pays ? `${nom} - ${pays}` : nom;
  };
  const typeDommageLabel = (v) => TYPES_DOMMAGE.find(t => t.value === v)?.label || v || '-';
  const catPermisLabels = (vals) => {
    if (!Array.isArray(vals) || vals.length === 0) return '-';
    return vals.map(v => CATEGORIES_PERMIS.find(c => c.value === v)?.value || v).join(' · ');
  };

  const sections = [
    {
      step: 0,
      title: t('recapitulatif.sections.contexte'),
      rows: [
        {
          label: t('recapitulatif.champs.type'), value: values.typeSinistre === 'SURVENU_TOGO'
            ? t('recapitulatif.champs.typeEt')
            : values.typeSinistre === 'SURVENU_ETRANGER'
              ? t('recapitulatif.champs.typeTe')
              : '-'
        },
        { label: t('recapitulatif.champs.paysGestionnaire'), value: paysLabel(values.paysGestionnaireTrackingId) },
        { label: t('recapitulatif.champs.paysEmetteur'), value: paysLabel(values.paysEmetteurTrackingId) },
        { label: t('recapitulatif.champs.bureauHomologue'), value: values.organismeHomologueTrackingId ? orgLabel(values.organismeHomologueTrackingId) : '-' },
        { label: t('recapitulatif.champs.numeroSinistreHomologue'), value: values.numeroSinistreHomologue || '-' },
      ],
    },
    {
      step: 1,
      title: t('recapitulatif.sections.contratAssureurs'),
      rows: [
        { label: t('recapitulatif.champs.numeroPolice'), value: values.numeroPolice || '-', mono: true },
        { label: t('recapitulatif.champs.numeroAttestation'), value: values.numeroAttestation || '-', mono: true },
        { label: t('recapitulatif.champs.assureurPrincipal'), value: orgLabel(values.organismeMembreTrackingId) },
        { label: t('recapitulatif.champs.numeroSinistreAssureur'), value: values.numeroSinistreAssureur || '-', mono: true },
        {
          label: t('recapitulatif.champs.assureursSecondaires'),
          value: (values.assureursSecondairesTrackingIds || []).length === 0
            ? '-'
            : values.assureursSecondairesTrackingIds.map(orgLabel).join(', ')
        },
        { label: t('recapitulatif.champs.dateEffet'), value: formatDate(values.dateEffet) },
        { label: t('recapitulatif.champs.dateEcheance'), value: formatDate(values.dateEcheance) },
      ],
    },
    {
      step: 1,
      title: t('recapitulatif.sections.assureVehicule'),
      rows: [
        { label: t('recapitulatif.champs.nature'), value: values.estPersonneMorale ? t('recapitulatif.champs.personneMorale') : t('recapitulatif.champs.personnePhysique') },
        {
          label: values.estPersonneMorale ? t('recapitulatif.champs.raisonSociale') : t('recapitulatif.champs.nomComplet'),
          value: [values.assureNom, values.assurePrenom].filter(Boolean).join(' ') || '-',
        },
        { label: t('recapitulatif.champs.profession'), value: values.assureProfession || '-' },
        { label: t('recapitulatif.champs.telephone'), value: values.assureTelephone || '-' },
        { label: t('recapitulatif.champs.marqueModele'), value: values.marqueVehicule || '-' },
        { label: t('recapitulatif.champs.immatriculation'), value: values.immatriculation || '-', mono: true },
        { label: t('recapitulatif.champs.proprietaire'), value: values.proprietaireVehicule || '-' },
        { label: t('recapitulatif.champs.prochaineVt'), value: formatDate(values.prochaineVT) },
        { label: t('recapitulatif.champs.placesABord'), value: `${values.capaciteVehicule ?? '-'} / ${values.nbPersonnesABord ?? '-'}` },
      ],
    },
    {
      step: 1,
      title: t('recapitulatif.sections.conducteur'),
      rows: values.conducteurEstAssure
        ? [
          { label: t('recapitulatif.champs.conducteur'), value: t('recapitulatif.champs.idemAssure') },
          { label: t('recapitulatif.champs.numeroPermis'), value: values.conducteurNumeroPermis || '-', mono: true },
          { label: t('recapitulatif.champs.categories'), value: catPermisLabels(values.conducteurCategoriesPermis) },
          { label: t('recapitulatif.champs.delivrance'), value: [formatDate(values.conducteurDateDelivrance), values.conducteurLieuDelivrance].filter(x => x && x !== '-').join(' · ') || '-' },
        ]
        : [
          { label: t('recapitulatif.champs.nom'), value: [values.conducteurNom, values.conducteurPrenom].filter(Boolean).join(' ') || '-' },
          { label: t('recapitulatif.champs.dateNaissance'), value: formatDate(values.conducteurDateNaissance) },
          { label: t('recapitulatif.champs.numeroPermis'), value: values.conducteurNumeroPermis || '-', mono: true },
          { label: t('recapitulatif.champs.categories'), value: catPermisLabels(values.conducteurCategoriesPermis) },
          { label: t('recapitulatif.champs.delivrance'), value: [formatDate(values.conducteurDateDelivrance), values.conducteurLieuDelivrance].filter(x => x && x !== '-').join(' · ') || '-' },
        ],
    },
    {
      step: 2,
      title: t('recapitulatif.sections.accident'),
      rows: [
        { label: t('recapitulatif.champs.dateHeureAccident'), value: `${formatDate(values.dateAccident)}${values.heureAccident ? ' · ' + formatTime(values.heureAccident) : ''}` },
        { label: t('recapitulatif.champs.villeCommune'), value: [values.ville, values.commune].filter(Boolean).join(' - ') || '-' },
        { label: t('recapitulatif.champs.lieuPrecis'), value: values.lieuAccident || '-' },
        { label: t('recapitulatif.champs.trajet'), value: [values.provenance, values.destination].filter(Boolean).join(t('recapitulatif.champs.trajetSeparateur')) || '-' },
        {
          label: t('recapitulatif.champs.agglomeration'), value: values.agglomeration === true ? t('commun.oui')
            : values.agglomeration === false ? t('commun.non') : '-'
        },
        { label: t('recapitulatif.champs.natureDommage'), value: typeDommageLabel(values.typeDommage) },
        { label: t('recapitulatif.champs.rapportPolice'), value: values.pvEtabli ? t('commun.oui') : t('commun.non') },
      ],
    },
    {
      step: 3,
      title: t('recapitulatif.sections.partiesAdverses', { nombre: adversaires.length }),
      rows: adversaires.length === 0
        ? [{ label: '', value: <span style={{ fontStyle: 'italic', color: T.inkMuted }}>{t('recapitulatif.champs.aucunePartieAdverse')}</span> }]
        : adversaires.map((a, i) => {
          const nature = natureDepuisAdversaire(a);
          const detail = [
            a.immatriculation,
            [a.marqueVehicule, a.modeleVehicule].filter(Boolean).join(' ') || null,
            (a.aRemorque || a.genreVehicule === 'REMORQUE')
              ? t('recapitulatif.champs.remorque', { immatriculation: a.remorqueImmatriculation || '' }).trim()
              : null,
            a.assureurAdverse,
          ].filter(Boolean).join(' · ');
          return {
            // Le libelle reste du TEXTE : c'est un gabarit de chaine, ou un element
            // React s'ecrirait « [object Object] ». L'icone, si elle est voulue ici,
            // devra passer par une propriete a part.
            label: `${nature.label} ${i + 1}`,
            value: [
              [a.nom, a.prenoms].filter(Boolean).join(' ') || nature.label,
              detail,
            ].filter(Boolean).join(' - ') || '-',
          };
        }),
    },
    {
      step: 4,
      title: t('recapitulatif.sections.victimes', { declarees: values.nombreVictimesAttendu || 0, fiches: victimes.length }),
      rows: [
        {
          label: t('recapitulatif.champs.bilanDeclare'),
          value: (values.nombreVictimesAttendu || 0) === 0
            ? '-'
            : [
              values.nombreBlessesAttendu ? t('recapitulatif.champs.blesses', { nombre: values.nombreBlessesAttendu }) : null,
              values.nombreDecedesAttendu ? t('recapitulatif.champs.deces', { nombre: values.nombreDecedesAttendu }) : null,
            ].filter(Boolean).join(' · ') || t('recapitulatif.champs.victimes', { nombre: values.nombreVictimesAttendu }),
        },
        ...(victimes.length === 0
          ? [{
            label: t('recapitulatif.champs.fiches'),
            value: <span style={{ fontStyle: 'italic', color: T.inkMuted }}>
              {(values.nombreVictimesAttendu || 0) > 0
                ? t('recapitulatif.champs.identitesInconnues')
                : t('recapitulatif.champs.aucuneFiche')}
            </span>,
          }]
          : victimes.map(v => ({
            label: t('recapitulatif.champs.victimeType', { type: v.typeVictime || t('recapitulatif.champs.neutre') }),
            value: v.dateNaissance
              ? t('recapitulatif.champs.identiteAge', { identite: `${v.nom} ${v.prenoms}`, age: dayjs().diff(v.dateNaissance, 'year') }) : `${v.nom} ${v.prenoms}`,
          }))),
      ],
    },
    /* Responsabilités : la part propre à chaque personne (« responsable à 30 % »),
       telle qu'on la saisit sur sa fiche. Ce ne sont pas les parts d'un partage :
       un passager non responsable et un conducteur responsable à 100 % ne se
       somment à rien de significatif - d'où plus de « total devrait faire 100 % ». */
    (() => {
      const lignes = [
        ...victimes.map((v, i) => ({ label: v.nom
            ? t('recapitulatif.champs.victimeNumeroNom', { numero: i + 1, nom: v.nom })
            : t('recapitulatif.champs.victimeNumero', { numero: i + 1 }), taux: v.tauxRc, })),
        ...adversaires.map((a, i) => ({ label: a.nom
            ? t('recapitulatif.champs.partieAdverseNumeroNom', { numero: i + 1, nom: a.nom })
            : t('recapitulatif.champs.partieAdverseNumero', { numero: i + 1 }), taux: a.tauxRc, })),
      ].filter((l) => l.taux !== null && l.taux !== undefined && l.taux !== '');
      // Rien de renseigné : la section n'apprendrait rien.
      if (lignes.length === 0) return null;
      return {
        step: 4,
        title: t('recapitulatif.sections.responsabilites'),
        rows: lignes.map((l) => ({
          label: l.label,
          value: Number(l.taux) > 0 ? t('recapitulatif.champs.responsableA', { taux: Number(l.taux) }) : t('recapitulatif.champs.nonResponsable'),
        })),
      };
    })(),
  ];

  /* Contrôle de la période de couverture - aligné avec le backend
     (Sinistre.isHorsPeriodeCouverture) : hors période, le dossier sera
     classé sans suite automatiquement à la création. */
  const dAcc = values.dateAccident ? dayjs(values.dateAccident) : null;
  const dEff = values.dateEffet ? dayjs(values.dateEffet) : null;
  const dEch = values.dateEcheance ? dayjs(values.dateEcheance) : null;
  const horsPeriodeCouverture = !!dAcc
    && ((!!dEff && dAcc.isBefore(dEff, 'day')) || (!!dEch && dAcc.isAfter(dEch, 'day')));

  return (
    <div style={{ maxWidth: 820 }}>
      <h1 style={H_TITLE}>{t('recapitulatif.titre')}</h1>
      <p style={H_SUB}>
        {t('recapitulatif.sousTitre')}
      </p>

      {horsPeriodeCouverture && (
        <div
          style={{
            marginTop: 20, padding: '14px 18px',
            background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: T.r.md, fontSize: 13, color: '#991b1b', lineHeight: 1.6,
          }}
        >
          <Trans
            t={t}
            i18nKey="recapitulatif.horsPeriode"
            values={{
              dateAccident: formatDate(values.dateAccident),
              dateEffet: formatDate(values.dateEffet),
              dateEcheance: formatDate(values.dateEcheance),
            }}
            components={{ 1: <strong />, 3: <strong /> }}
          />
        </div>
      )}

      <div style={{ marginTop: 36 }}>
        {sections.filter(Boolean).map((section, idx) => (
          <div
            key={section.title}
            style={{
              borderTop: idx === 0 ? `1px solid ${T.line}` : 'none',
              borderBottom: `1px solid ${T.line}`,
              padding: '22px 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={SECTION_LABEL}>{section.title}</div>
              <button
                type="button"
                onClick={() => goToStep(section.step)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  border: 'none', background: 'none', color: T.brand,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  letterSpacing: '0.01em',
                }}
              >
                <EditOutlined style={{ fontSize: 11 }} /> {t('commun.modifier')}
              </button>
            </div>

            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '200px 1fr', rowGap: 10, columnGap: 24 }}>
              {section.rows.map((row, i) => (
                <React.Fragment key={i}>
                  <dt style={{ fontSize: 12.5, color: T.inkMuted, paddingTop: 2 }}>{row.label}</dt>
                  <dd
                    style={{
                      margin: 0, fontSize: 13.5, color: T.ink, fontWeight: 500,
                      fontFamily: row.mono ? T.fontMono : T.fontBody,
                      letterSpacing: row.mono ? '0.02em' : undefined,
                    }}
                  >
                    {row.value}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {/* Bloc déclarant */}
      <DeclarantBlock form={form} organismes={organismes} />

      {piecesTe && <PiecesHomologueBlock pieces={piecesHomologue} onChange={setPiecesHomologue} />}

      <div
        style={{
          marginTop: 20, padding: '14px 18px',
          background: T.brandSoft, border: `1px solid ${T.brandLine}`,
          borderRadius: T.r.md,
          fontSize: 13, color: T.inkSoft, lineHeight: 1.6,
        }}
      >
        <Trans
          t={t}
          i18nKey={piecesTe ? 'recapitulatif.avisEnregistreTe' : 'recapitulatif.avisDeclare'}
          components={{ 1: <strong />, 3: <strong /> }}
        />
      </div>
    </div>
  );
}
